import {FormEvent, useState} from "react";
import {Alert, Form} from "react-bootstrap";
import {Link, useNavigate} from "react-router";
import {SupportApi} from "../../api/SupportApi";
import {SUPPORT_MESSAGE_MAX_LENGTH, SUPPORT_NAME_MAX_LENGTH, SUPPORT_SUBJECT_MAX_LENGTH} from "../../api/models";
import {useAuth} from "../../auth/AuthContext";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {Field, FieldControl} from "../../components/Field";
import {SubmitButton} from "../../components/SubmitButton";
import {announce} from "../../components/announce";
import {emailProblem} from "../../components/format";
import {turnstileAvailable} from "../../components/Turnstile";
import {useTurnstile} from "../../components/useTurnstile";
import {format, rich, support} from "../../i18n";

const messages = support.contactPage;
const fields = support.newTicketPage;

// Contact us: NewSupportTicketPage's form for a ticket that isn't about a license, worded for questions before buying
// and anything else. No license to choose and no files (they can be added from the ticket). Signed out: name and email
// too, and the API emails a private link to the ticket; signed in, it opens the ticket. Turnstile's bot check first.
// Prerendered (scripts/prerender.mjs) for search engines, as a visitor who isn't signed in sees it.
export function ContactPage() {
    const navigate = useNavigate();
    const {user, loading: authLoading} = useAuth();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const turnstile = useTurnstile();
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const [sentTo, setSentTo] = useState<string>();

    const problems = {
        name: !user && !name.trim() ? fields.problems.name : undefined,
        email: !user ? (!email.trim() ? fields.problems.email : emailProblem(email)) : undefined,
        subject: !subject.trim() ? fields.problems.subject : undefined,
        body: !body.trim() ? fields.problems.body : undefined
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (Object.values(problems).some(Boolean)) {
            return;
        }
        setBusy(true);
        setError(undefined);
        const turnstileToken = await turnstile.waitForToken();
        if (!turnstileToken) {
            setBusy(false);
            return;
        }
        try {
            const opened = await SupportApi.open({
                ...(user ? {} : { name: name.trim(), email: email.trim() }),
                subject: subject.trim(),
                body: body.trim(),
                turnstileToken
            }, []);
            announce(messages.sent);
            if (user) {
                navigate(`/support/${opened.id}`);
            } else {
                setSentTo(email.trim());
            }
        } catch (err) {
            setError(err);
            turnstile.reset();
        } finally {
            setBusy(false);
        }
    };

    const header = <PageHeader title={messages.title} description={messages.description}/>;
    if (authLoading) {
        return <>{header}<LoadingSpinner/></>;
    }
    if (!turnstileAvailable()) {
        return <>{header}<Alert variant="secondary">{messages.unavailable}</Alert></>;
    }
    if (sentTo) {
        return <>{header}<Alert variant="success" role="status">{format(messages.checkEmail, { email: sentTo })}</Alert></>;
    }

    return (
        <>
            {header}
            {!user && (
                <p className="text-body-secondary">
                    {rich(messages.signedOutIntro, { link: text => <Link to={`/login?returnTo=${encodeURIComponent('/contact')}`}>{text}</Link> })}
                </p>
            )}
            <Form noValidate onSubmit={submit} className="support-form">
                <ErrorAlert error={error} shownFields={['name', 'email', 'subject', 'body']} messages={{
                    429: user ? messages.tooMany : messages.tooManySignedOut,
                    turnstile_failed: fields.turnstileFailed,
                    503: messages.unavailable
                }}/>
                {!user && (
                    <>
                        <Field className="mb-3" controlId="contact-name" label={fields.name} error={(validated && problems.name) || fieldError(error, 'name')}>
                            <FieldControl value={name} onChange={e => setName(e.target.value)} maxLength={SUPPORT_NAME_MAX_LENGTH} autoComplete="name" disabled={busy}/>
                        </Field>
                        <Field className="mb-3" controlId="contact-email" label={fields.email} help={messages.emailHelp}
                               error={(validated && problems.email) || fieldError(error, 'email')}>
                            <FieldControl type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" disabled={busy}/>
                        </Field>
                    </>
                )}
                <Field className="mb-3" controlId="contact-subject" label={fields.subject} error={(validated && problems.subject) || fieldError(error, 'subject')}>
                    <FieldControl value={subject} onChange={e => setSubject(e.target.value)} maxLength={SUPPORT_SUBJECT_MAX_LENGTH} disabled={busy}/>
                </Field>
                <Field className="mb-4" controlId="contact-body" label={fields.body} help={fields.englishOnly}
                       error={(validated && problems.body) || fieldError(error, 'body')}>
                    <FieldControl as="textarea" rows={8} value={body} onChange={e => setBody(e.target.value)} maxLength={SUPPORT_MESSAGE_MAX_LENGTH} disabled={busy}/>
                </Field>
                <div className="mb-3">
                    {turnstile.widget}
                    {turnstile.failed && <Alert variant="warning">{fields.turnstileUnavailable}</Alert>}
                </div>
                <SubmitButton busy={busy}>{messages.submit}</SubmitButton>
            </Form>
        </>
    );
}
